import axios from 'axios';
import {getPostHeaders} from './headers';
import {RESOURCES as resources} from '../orderResources';

// The message for a successful response that carried no body.
export const EMPTY_RESPONSE = 'The server returned an empty response';

/**
 * Turn whatever an order or item request failed with into text for the user.
 *
 * postOrder and postItem used to reject with the server's `error` string when
 * there was one and otherwise with the raw axios response or XMLHttpRequest,
 * which the pages rendered as "[object Object]" (parrot-media/portal/main#319).
 * Every rejection now goes through here, so callers always get a string.
 *
 * @param {*} failure a string, an axios error.response, an XMLHttpRequest, or an Error
 * @return {string}
 */
export const failureMessage = (failure) => {
    if (typeof failure === 'string' && failure.trim() !== '') {
        return failure;
    }
    if (failure && typeof failure === 'object') {
        const serverError = failure.data?.error;
        if (typeof serverError === 'string' && serverError.trim() !== '') {
            return serverError;
        }
        // An axios response outside 2xx
        if (typeof failure.status === 'number' && failure.status > 0) {
            return `The server responded with ${failure.status}${failure.statusText ? ' ' + failure.statusText : ''}`;
        }
        // A request that got no response: offline, timeout, CORS, DNS
        if ('readyState' in failure || failure.status === 0) {
            return 'Could not reach the server. Please check your connection and try again';
        }
        if (typeof failure.message === 'string' && failure.message.trim() !== '') {
            return failure.message;
        }
    }
    return 'Unknown error';
};

/**
 * Post a request to Start and Create an Order
 *
 * @param type
 * @param data
 * @return {Promise<unknown>}
 */
export const postOrder = (type, data) => {
    return new Promise(function (resolve, reject) {
        if (data) {

            const onFailure = function (result) {
                return reject(failureMessage(result));
            }

            const onSuccess = function (result) {
                return resolve(result);
            }

            const params = {
                data: data
            };
            let cfg;
            switch (type) {
                case 'startOrder':
                    cfg = resources.OrderStart.CREATE(params);
                    break;
                case 'createOrder':
                    cfg = resources.OrderCreate.CREATE(params);
                    break;
                case 'updateOrder':
                    cfg = resources.Order.UPDATE(params);
                    break;
                default:
                    break;
            }

            // Set headers
            const headers = getPostHeaders(true);

            try {
                axios({
                    method: cfg.method,
                    url: cfg.uri,
                    data: cfg.data,
                    headers: headers
                })
                    .then((result) => {
                        // Settle on every success: a 2xx with no body used to leave
                        // the promise pending forever (#13). Callers need the body
                        // (the new order's id, the saved item), so it is a failure.
                        if (result && result.data) {
                            return onSuccess(result.data);
                        }
                        return onFailure(EMPTY_RESPONSE);
                    })
                    .catch((error) => {
                        if (error.response) {
                            // Server response outside 2xx
                            onFailure(error.response);
                        } else if (error.request) {
                            onFailure(error.request);
                        } else {
                            onFailure(error);
                        }
                    });
            } catch (err) {
                console.log('Order try/catch error: ', err);
                onFailure(err);
            }
        } else {
            return resolve();
        }
    });
};

/**
 * Post a request to Add or Update a Product in an Order
 *
 * @param type
 * @param data
 * @return {Promise<unknown>}
 */
export const postItem = (type, data) => {
    return new Promise(function (resolve, reject) {
        if (data) {

            const onFailure = function (result) {
                return reject(failureMessage(result));
            }

            const onSuccess = function (result) {
                return resolve(result);
            }

            let productToPost = {
                order_id: data.order_id,
                product_id: data.product_id,
                qty: data.qty
            };
            if (type === 'updateItem') {
                productToPost.id = data.id;
                productToPost.new_qty = data.new_qty;
            }
            const params = {
                data: productToPost
            };
            let cfg;
            switch (type) {
                case 'addItem':
                    cfg = resources.OrderItem.CREATE(params);
                    break;
                case 'updateItem':
                    cfg = resources.OrderItem.UPDATE(params);
                    break;
                default:
                    break;
            }

            // Set headers
            const headers = getPostHeaders(true);

            try {
                axios({
                    method: cfg.method,
                    url: cfg.uri,
                    data: cfg.data,
                    headers: headers
                })
                    .then((result) => {
                        // Settle on every success: a 2xx with no body used to leave
                        // the promise pending forever (#13). Callers need the body
                        // (the new order's id, the saved item), so it is a failure.
                        if (result && result.data) {
                            return onSuccess(result.data);
                        }
                        return onFailure(EMPTY_RESPONSE);
                    })
                    .catch((error) => {
                        if (error.response) {
                            // Server response outside 2xx
                            onFailure(error.response);
                        } else if (error.request) {
                            // No response
                            onFailure(error.request);
                        } else {
                            onFailure(error);
                        }
                    });
            } catch (err) {
                console.log('Order postItem try/catch error: ', err);
                onFailure(err);
            }
        } else {
            return resolve();
        }
    });
};
